// The Search pane's result normalisation, kept out of the view: one ledger row
// becomes the two lines the result list shows and the point/record a click
// opens. This is the finder's old job, moved out of MapReview.vue and made
// exhaustive.
//
// Exhaustiveness is the point. The finder used to handle Sites, Features,
// Treatments and Observations and let every other table fall through to the
// Observation branch, so a Trap Location, a Trap Instance, a Mosquito Count, a
// Trap Sample and a Zone all read as Observations — the wrong title, the wrong
// detail and, for a Zone, a Site that is not even one of its fields. Every one
// of the nine `MAP_REVIEW_TABLES` now names its own reading, and a table this
// module does not know throws instead of quietly borrowing another table's
// labels.
import { formatRelativeTime } from "@/format";
import { featureTypeLabel } from "@/lib/featureType";

import type { SearchGroup, SearchResult } from "@/components/search/types";
import type {
	AddressSearchResult,
	MapReviewFeatureRow,
	MapReviewObservationRow,
	MapReviewQueryEnvelope,
	MapReviewRow,
	MapReviewSiteRow,
	MapReviewSpeciesCountRow,
	MapReviewTable,
	MapReviewTrapInstanceRow,
	MapReviewTrapSampleRow,
	MapReviewTrapStationRow,
	MapReviewTreatmentRow,
	MapReviewZoneRow,
} from "@/type/api";

import {
	MAP_REVIEW_TABLE_LABELS,
	SAMPLE_STATE_LABELS,
} from "@/components/map-review/columns";

/** How many matches of one table's search the pane lists before it says how
 * many more there are. The group's total is the server's `filtered_total`, so a
 * capped list never reads as the whole answer. */
export const RECORD_SEARCH_LIMIT = 5;

/** One record found by the search, carried as the result's payload: the table
 * it belongs to, its id, the map point to fly to (null when the row has none —
 * the details open with the map left where it is) and the ledger row itself.
 * The row travels whole because an Observation's popup renders it directly:
 * the search never narrows the ledger page the popup would otherwise read the
 * row from. */
export interface RecordSearchResult {
	table: MapReviewTable;
	id: number;
	anchor: { lng: number; lat: number } | null;
	row: MapReviewRow;
}

/** What the pane's `select` emit carries: a record result (the table, id, point
 * and row the view needs to fly and open) or an address result. The view owns
 * both side effects, so a payload never touches the map or the store here. */
export type SearchSelection =
	| { kind: "address"; result: AddressSearchResult }
	| {
			kind: "record";
			table: MapReviewTable;
			id: number;
			anchor: { lng: number; lat: number } | null;
			row: MapReviewRow;
	  };

/** The row's map point, when it has one. The anchor is the row's own
 * latitude/longitude; a row without both flies nowhere and opens its details in
 * place. */
function anchorFor(row: MapReviewRow): { lng: number; lat: number } | null {
	return row.longitude != null && row.latitude != null
		? { lng: row.longitude, lat: row.latitude }
		: null;
}

/** Build one result row: a stable key (table + id, so two tables' same id never
 * collide), the two lines, and the payload a selection reads. An empty detail is
 * omitted rather than rendered as a blank line. */
function resultFor(
	table: MapReviewTable,
	row: MapReviewRow,
	title: string,
	detail: string,
): SearchResult<RecordSearchResult> {
	return {
		key: `${table}-${row.id}`,
		title,
		detail: detail || undefined,
		payload: { table, id: row.id, anchor: anchorFor(row), row },
	};
}

/** A record's two lines, in the table's own terms. Each branch reads the row
 * shape `ts/type/api.ts` names for that table; the reads are the same facts the
 * ledger's identity column shows, so a result is recognisable as the row the
 * click opens.
 *
 * The Site is the one table named by its own label — it *is* the address — while
 * every child record is named by its Site and distinguished by its own fact: a
 * Feature's habitat, a Treatment's product and age, an Observation's habitat and
 * age, the trap family's own identifiers and states. */
export function recordSearchResult(
	table: MapReviewTable,
	row: MapReviewRow,
): SearchResult<RecordSearchResult> {
	switch (table) {
		case "site": {
			const site = row as MapReviewSiteRow;
			return resultFor(table, row, site.label || "Site", site.address);
		}
		case "feature": {
			const feature = row as MapReviewFeatureRow;
			return resultFor(
				table,
				row,
				featureTypeLabel(feature.type),
				[feature.site, `Feature #${row.id}`].filter(Boolean).join(" · "),
			);
		}
		case "treatment": {
			const treatment = row as MapReviewTreatmentRow;
			return resultFor(
				table,
				row,
				treatment.product || treatment.intervention || "Treatment",
				[
					treatment.site || treatment.site_address,
					formatRelativeTime(treatment.start_datetime),
				]
					.filter(Boolean)
					.join(" · "),
			);
		}
		case "observation": {
			const observation = row as MapReviewObservationRow;
			return resultFor(
				table,
				row,
				observation.site || "Observation",
				[
					featureTypeLabel(observation.feature),
					formatRelativeTime(observation.observed_at),
				]
					.filter(Boolean)
					.join(" · "),
			);
		}
		case "trap_station": {
			// A Trap Location has no label of its own: the ledger identifies it by
			// its id, and its Site is the row's first line, exactly as the trap
			// table reads it.
			const station = row as MapReviewTrapStationRow;
			return resultFor(
				table,
				row,
				station.site || "Trap Location",
				[
					`Trap Location #${row.id}`,
					station.active ? "Active" : "Retired",
				].join(" · "),
			);
		}
		case "trap_instance": {
			const instance = row as MapReviewTrapInstanceRow;
			return resultFor(
				table,
				row,
				instance.site || "Trap Instance",
				[instance.trap_type, formatRelativeTime(instance.date)]
					.filter(Boolean)
					.join(" · "),
			);
		}
		case "species_count": {
			// One count is one species × sex × number; all three are the row's own
			// facts and none of them is a Site, so the Site stays the title.
			const count = row as MapReviewSpeciesCountRow;
			return resultFor(
				table,
				row,
				count.site || "Mosquito count",
				[count.species, count.sex, count.count.toLocaleString()]
					.filter(Boolean)
					.join(" · "),
			);
		}
		case "trap_sample": {
			// A sample's identity is the district's handwriting on the container,
			// the one string this bench reads and searches; its Site and analysis
			// state are the second line.
			const sample = row as MapReviewTrapSampleRow;
			return resultFor(
				table,
				row,
				sample.label || "Trap sample",
				[sample.site, SAMPLE_STATE_LABELS[sample.state] ?? sample.state]
					.filter(Boolean)
					.join(" · "),
			);
		}
		case "zone": {
			const zone = row as MapReviewZoneRow;
			return resultFor(
				table,
				row,
				zone.name || "Zone",
				[zone.active ? "Active" : "Retired", formatRelativeTime(zone.created)]
					.filter(Boolean)
					.join(" · "),
			);
		}
		default: {
			// The lab's Pools ledger is a MapReviewTable but not one of the
			// workbench's tabs (`MAP_REVIEW_TABLES`), so the Search pane never
			// asks for it. Throwing rather than inventing a reading is what keeps
			// a future table from silently inheriting another table's labels —
			// the bug this module fixes.
			throw new Error(`no search reading for table: ${table}`);
		}
	}
}

/** One table's matches, with the table's own group label and the server's full
 * match count so the list can say how many were cut off by the limit. */
export function recordGroup(
	table: MapReviewTable,
	response: MapReviewQueryEnvelope<MapReviewRow>,
): SearchGroup<RecordSearchResult> {
	return {
		key: table,
		label: MAP_REVIEW_TABLE_LABELS[table],
		total: response.metadata.filtered_total,
		results: response.items.map((row) => recordSearchResult(table, row)),
	};
}

/** A table whose query itself failed. The group stays in the list — named, with
 * a failure line — rather than vanishing, so the operator sees which table could
 * not be searched instead of silently losing its matches. */
export function failedRecordGroup(
	table: MapReviewTable,
): SearchGroup<RecordSearchResult> {
	return {
		key: table,
		label: MAP_REVIEW_TABLE_LABELS[table],
		results: [],
		failed: true,
	};
}
